/** Lightweight helpers for JWT-based auth used across the app. */

/** Get the stored JWT token, or null if not logged in. */
export const getToken = (): string | null => localStorage.getItem('token');

/** Whether the user currently has a valid JWT stored. */
export const isLoggedIn = (): boolean => !!getToken();

/**
 * Build a standard `Authorization` header object.
 * Returns `undefined` when no token is stored so callers can
 * spread it safely: `headers: { 'Content-Type': '…', ...authHeader() }`.
 */
export const authHeader = (): Record<string, string> | undefined => {
	const token = getToken();
	return token ? { Authorization: `Bearer ${token}` } : undefined;
};

/**
 * Clear the stored token and redirect to the login page.
 * Call this when the backend returns 401/403 due to an invalid or expired token.
 */
export const handleAuthError = (returnUrl?: string): void => {
	localStorage.removeItem('token');
	localStorage.removeItem('user');
	const state = returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : '';
	window.location.href = `/auth${state}`;
};

/**
 * Remove ALL app-related data from localStorage.
 * Used on sign-out and account deletion to satisfy GDPR Art. 17.
 */
export const clearAllAppData = (): void => {
	// Collect all keys first to avoid mutation during iteration
	const keys: string[] = [];
	for (let i = 0; i < localStorage.length; i++) {
		const key = localStorage.key(i);
		if (key) keys.push(key);
	}
	keys.forEach((key) => localStorage.removeItem(key));
};
